import fileDownload from "js-file-download";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/Button";
import ComboBoxStation from "../../../components/ComboBoxStation";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Input, Select } from "../../../components/Inputs";
import Loading from "../../../components/Loading";
import ModalCertificate from "../../../components/Modal/ModalCertificate";
import Pill from "../../../components/Pill";
import { resourceTypes } from "../../../constants/selects";
import { monthsAndResourceTypes } from "../../../constants/translations";
import { API_EXPORT } from "../../../helpers/api";
import { convertAmountToFrontend } from "../../../helpers/convert";
import { setNotification } from "../../../helpers/notification";
import {
  getActiveCertificatesRequest,
  setCertificateFilterExportEndDate,
  setCertificateFilterExportStartDate,
  setCertificateFilterProductionPeriod,
  setCertificateFilterResourceType,
  setCertificateFilterStation,
} from "../../../redux/actions/certificates";

export default function ActiveCertificates({ t }) {
  const dispatch = useDispatch();

  const { activeList: certificates, totalPageCount } = useSelector(
    (state) => state.certificates
  );

  const loading = useSelector((state) => state.certificates.loading);
  // Filters
  const filterStation = useSelector(
    (state) => state.certificates.filterStation
  );
  const filterResourceType = useSelector(
    (state) => state.certificates.filterResourceType
  );
  const filterProductionPeriodMonth = useSelector(
    (state) => state.certificates.filterProductionPeriodMonth
  );
  const filterProductionPeriodYear = useSelector(
    (state) => state.certificates.filterProductionPeriodYear
  );

  const filterExportStartDateMonth = useSelector(
    (state) => state.certificates.filterExportStartDateMonth
  );
  const filterExportStartDateYear = useSelector(
    (state) => state.certificates.filterExportStartDateYear
  );

  const filterExportEndDateMonth = useSelector(
    (state) => state.certificates.filterExportEndDateMonth
  );
  const filterExportEndDateYear = useSelector(
    (state) => state.certificates.filterExportEndDateYear
  );

  const [inspectedCertificateId, setInspectedCertificateId] = useState();
  const [filterStatus, setFilterStatus] = useState(["ACTIVE", "BLOCK"]);
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadFilter, setDownloadFilter] = useState({});
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activePage, setActivePage] = useState(0);
  const translations = monthsAndResourceTypes(t);
  const selectResourceType = resourceTypes(t);

  const documentCount = useSelector(
    (state) => state.certificates.documentCount
  );
  const blockedDocumentCount = useSelector(
    (state) => state.certificates.blockedDocumentCount
  );

  const selectStatus = [
    {
      name: t("pages.default.certificates.filters.allStatus"),
      value: ["ACTIVE", "BLOCK"],
    },
    {
      name: t("ui.default.codes.ACTIVE"),
      value: "ACTIVE",
    },
    {
      name: t("ui.default.codes.BLOCK"),
      value: "BLOCK",
    },
  ];

  const activeCertificatesColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        return `${translations[row.productionMonth]}/${row.productionYear}`;
      },
    },
    {
      name: t("pages.default.certificates.table.exportPeriod"),
      selector: "exportDate",
      sortable: true,
      cell: (row) => moment(row.exportDate).format("DD-MM-YYYY HH:mm:ss"),
    },
    {
      name: t("pages.default.certificates.table.durationOfUse"),
      selector: "durationOfUse",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.documentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t("pages.default.certificates.table.blockedAmount"),
      selector: "blockedAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.blockedAmount);
      },
    },
    {
      name: t("pages.default.certificates.table.status"),
      selector: "certificateStatus",
      sortable: true,
      style: { overflow: "visible" },
      cell: (row) => {
        return (
          <Pill
            label={translations[row.certificateStatus]}
            type={row.certificateStatus === "ACTIVE" ? "success" : "important"}
            tooltip={row.certificateStatus === "ACTIVE" ? "" : row.description}
          />
        );
      },
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      cell: (row) => {
        return (
          <>
            <Button
              title={t("ui.default.review")}
              suffixIcon="search"
              type="secondary"
              onClick={() => inspectCertificate(row)}
            />
          </>
        );
      },
    },
  ];

  useEffect(() => {
    filterCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterChange = (e) => {
    let splitted = [];
    let payload = {};
    splitted = e.target.value.split("-");
    payload = {
      month: splitted[1] || "",
      year: splitted[0] || "",
    };
    switch (e.target.name) {
      case "exportStartDate":
        dispatch(setCertificateFilterExportStartDate(payload));
        break;
      case "exportEndDate":
        dispatch(setCertificateFilterExportEndDate(payload));
        break;
      case "productionPeriod":
        dispatch(setCertificateFilterProductionPeriod(payload));
        break;
      case "resourceType":
        dispatch(setCertificateFilterResourceType(e.target.value));
        break;
      case "filterStatus":
        setFilterStatus(e.target.value);
        break;
      default:
        break;
    }
  };

  const selectStation = (station) => {
    dispatch(setCertificateFilterStation(station));
  };

  const inspectCertificate = (row) => {
    setInspectedCertificateId(row.id);
  };

  const filterCertificates = () => {
    const filters = {
      stationId: filterStation ? filterStation.stationId : "",
      resourceType: filterResourceType,
      month: filterProductionPeriodMonth,
      year: filterProductionPeriodYear,
      exportStartDateMonth: filterExportStartDateMonth,
      exportEndDateMonth: filterExportEndDateMonth,
      exportStartDateYear: filterExportStartDateYear,

      certificateStatus: filterStatus,
    };

    console.log("filters " + JSON.stringify(filters));

    dispatch(
      getActiveCertificatesRequest({
        ...filters,
      })
    );
    setDownloadFilter({
      ...filters,
    });
  };

  const downloadCertificates = async () => {
    const { stationId, resourceType, month, year, certificateStatus } =
      downloadFilter;
    setExportLoading(true);
    try {
      const res = await API_EXPORT("certificate/export/excel", {
        stationId,
        resourceType: resourceType !== "-" ? resourceType : "",
        month: month,
        year: year,
        certificateStatus,
        lang: window.localStorage.i18nextLng,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };
  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    const filters = {
      stationId: filterStation ? filterStation.stationId : "",
      resourceType: filterResourceType,
      month: filterProductionPeriodMonth,
      year: filterProductionPeriodYear,
      exportStartDateMonth: filterExportStartDateMonth,
      exportEndDateMonth: filterExportEndDateMonth,
      exportStartDateYear: filterExportStartDateYear,
      exportEndDateYear: filterExportEndDateYear,
      certificateStatus: filterStatus,
      page: activePage,
      selector,
      direction,
    };
    dispatch(
      getActiveCertificatesRequest({
        ...filters,
      })
    );
  };

  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.certificates.activeTitle")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--certificates-filter">
          <ComboBoxStation
            t={t}
            selectedStation={filterStation}
            selectStation={selectStation}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.certificates.filters.allProductionPeriod"
            )}
            name="productionPeriod"
            placement="bottom-end"
            value={
              filterProductionPeriodYear
                ? `${filterProductionPeriodYear}-${filterProductionPeriodMonth}`
                : ""
            }
            onChange={filterChange}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.certificates.filters.exportStartDate"
            )}
            name="exportStartDate"
            placement="bottom-end"
            value={
              filterExportStartDateYear
                ? `${filterExportStartDateYear}-${filterExportStartDateMonth}`
                : ""
            }
            onChange={filterChange}
          />
          <Select
            options={selectResourceType}
            name="resourceType"
            defaultValue={filterResourceType}
            onChange={filterChange}
          />
          <Select
            options={selectStatus}
            name="filterStatus"
            defaultValue={filterStatus}
            onChange={filterChange}
          />
          <Button
            type="primary"
            label={t("ui.default.filter")}
            onClick={filterCertificates}
            id="c-filter"
          />
          <Button
            type="secondary"
            label={t("ui.default.download")}
            suffixIcon="download"
            onClick={downloadCertificates}
            disabled={exportLoading}
            id="c-download"
          />
        </div>
        {certificates.length > 0 && !loading && (
          // <DataTable
          //   rowKey='id'
          //   columns={activeCertificatesColumns}
          //   data={certificates}
          //   subTotals={[{
          //     totalCount: documentCount.totalCount,
          //     countInPage: documentCount.countInPage,
          //     sumIndex: 5
          //   },
          //   {
          //     totalCount: blockedDocumentCount.totalCount,
          //     countInPage: blockedDocumentCount.countInPage,
          //     sumIndex: 6
          //   }]}
          //   defaultSortedField={'productionPeriod'}
          //   defaultSortAsc={false}
          //   paginationPerPage={15}
          //   pagination
          // />
          <DataTableServerSideRendering
            columns={activeCertificatesColumns}
            data={certificates}
            subTotals={[
              {
                totalCount: documentCount.totalCount,
                countInPage: documentCount.countInPage,
                sumIndex: 6,
              },
              {
                totalCount: blockedDocumentCount.totalCount,
                countInPage: blockedDocumentCount.countInPage,
                sumIndex: 7,
              },
            ]}
            defaultSortedField={"productionPeriod"}
            defaultSortAsc={false}
            paginationPerPage={15}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={totalPageCount}
            handleFetchData={handleClick}
          />
        )}
        {certificates.length === 0 && !loading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.certificates.activeNotFound")}
          />
        )}
        {loading && (
          <Loading text={t("pages.default.certificates.activeLoading")} />
        )}
      </div>
      {inspectedCertificateId && (
        <ModalCertificate
          t={t}
          certificateId={inspectedCertificateId}
          isOpen={inspectedCertificateId ? true : false}
          toggle={() => setInspectedCertificateId()}
          difCalculate={true}
        />
      )}
    </>
  );
}
